import React, { useEffect, useState } from "react";
import { BellRing, BellOff, CheckCircle2, Share, SquarePlus } from "lucide-react";

import { Button } from "../ui/button";
import {
  dismissPushOffer,
  enablePushNotifications,
  getPushStatus,
  isPushOfferDismissed,
  shouldOfferPush,
  type PushStatus,
} from "../../lib/push";
import { isNativeApp } from "../../lib/native";
import { getWebPushMissingFeatures, isIosDevice } from "../../lib/webPush";

interface PushOptInCardProps {
  // compact: only while permission hasn't been asked (notification centre).
  // full: always shows this device's state (settings page).
  variant: "compact" | "full";
}

// Explains what push notifications are for before the system permission
// prompt, which Android 13+, iOS and browsers only show once. In the app this
// is native push; on the website it's web push, and iPhone visitors are shown
// how to add the site to their Home Screen first.
export function PushOptInCard({ variant }: PushOptInCardProps) {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getPushStatus(), shouldOfferPush()]).then(([nextStatus, offer]) => {
      if (cancelled) return;
      setStatus(nextStatus);
      // A browser that can't do push says so (on the settings page always, in
      // the notification centre on iPhones, where people expect it to work).
      const explainUnsupported =
        nextStatus === "unsupported" && !isNativeApp && (variant === "full" || (isIosDevice() && !isPushOfferDismissed()));
      setVisible(variant === "full" ? nextStatus !== "unsupported" || explainUnsupported : offer || explainUnsupported);
    });
    return () => {
      cancelled = true;
    };
  }, [variant]);

  if (!visible || !status) return null;

  const handleEnable = async () => {
    setBusy(true);
    try {
      const next = await enablePushNotifications();
      setStatus(next);
      if (variant === "compact" && next !== "prompt") setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  const handleNotNow = () => {
    dismissPushOffer();
    setVisible(false);
  };

  const box = "m-4 rounded-2xl border p-4 text-sm";

  if (status === "granted") {
    return (
      <div className={`${box} flex items-start gap-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200`}>
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
        <p>Push notifications are on for this device.</p>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className={`${box} flex items-start gap-3 border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200`}>
        <BellOff className="mt-0.5 h-5 w-5 shrink-0" />
        {isNativeApp ? (
          <p>
            Notifications are blocked for The Deepam. To get them, open your phone's <strong>Settings</strong>, find{" "}
            <strong>The Deepam</strong>, and turn on <strong>Notifications</strong>.
          </p>
        ) : (
          <p>
            Notifications are blocked for this site. To get them, open your browser's site settings for thedeepam.com
            (the icon next to the address), allow <strong>Notifications</strong>, then reload the page.
          </p>
        )}
      </div>
    );
  }

  if (status === "unsupported") {
    const missing = getWebPushMissingFeatures();
    return (
      <div className={`${box} flex items-start gap-3 border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200`}>
        <BellOff className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0">
          {isIosDevice() ? (
            <p>
              This iPhone can't get notifications from The Deepam here. They need <strong>iOS 16.4 or newer</strong>,
              The Deepam <strong>opened from your Home Screen</strong> (not a Safari tab), and a real iPhone (the iOS
              Simulator doesn't support them).
            </p>
          ) : (
            <p>This browser can't receive notifications. Try Chrome, Edge, Firefox or Safari on a computer or phone.</p>
          )}
          {missing.length > 0 && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Not available: {missing.join(", ")}</p>
          )}
          {variant === "compact" && (
            <Button size="sm" variant="ghost" onClick={handleNotNow} className="mt-2 rounded-full">
              Got it
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (status === "unconfigured") {
    return variant === "full" ? (
      <div className={`${box} border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300`}>
        Push notifications aren't available here yet.
      </div>
    ) : null;
  }

  if (status === "needs-install") {
    return (
      <div className={`${box} border-amber-200 bg-amber-50 text-slate-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-slate-100`}>
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-500 text-white">
            <BellRing className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Get notifications on your iPhone</p>
            <p className="mt-0.5 text-slate-600 dark:text-slate-300">
              iPhone only allows notifications for sites on your Home Screen:
            </p>
            <ol className="mt-2 space-y-1.5 text-slate-700 dark:text-slate-200">
              <li className="flex items-center gap-1.5">
                1. Tap <Share className="h-4 w-4" aria-label="Share" /> <strong>Share</strong> in Safari
              </li>
              <li className="flex items-center gap-1.5">
                2. Choose <SquarePlus className="h-4 w-4" aria-hidden="true" /> <strong>Add to Home Screen</strong>
              </li>
              <li>
                3. Open The Deepam from your Home Screen and tap <strong>Turn on</strong> here
              </li>
            </ol>
            {variant === "compact" && (
              <Button size="sm" variant="ghost" onClick={handleNotNow} className="mt-2 rounded-full">
                Not now
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${box} border-amber-200 bg-amber-50 text-slate-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-slate-100`}>
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-500 text-white">
          <BellRing className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Turn on notifications</p>
          <p className="mt-0.5 text-slate-600 dark:text-slate-300">
            Know when someone you follow shares something new, and when people follow, like, reply to or mention you.
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={handleEnable} disabled={busy} className="rounded-full">
              {busy ? "Turning on..." : "Turn on"}
            </Button>
            {variant === "compact" && (
              <Button size="sm" variant="ghost" onClick={handleNotNow} disabled={busy} className="rounded-full">
                Not now
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
